import React, { FC } from "react";

import { useParams } from "react-router-dom";
import { DatabaseEnum } from "@Shape-Digital/kudzu-data/lib/types/common";
import Button from "../../Unknown/Button";
import Card from "../../Unknown/Card";
import CardContent from "../../Unknown/CardContent";
import CardHeader from "../../Unknown/CardHeader";
import Box from "../../Unknown/Box";
import Grid from "../../Unknown/Grid";
import Typography from "../../Unknown/Typography";
import AppointmentPaymentGrid from "../AppointmentPaymentGrid";
import { AppointmentPayment } from "../AppointmentPaymentGrid/types";
import useStyles from "./useStyles";
import useTranslations from "./useTranslations";
import AppointmentRefundGrid from "../AppointmentRefundGrid";
import { AppointmentRefund } from "../AppointmentRefundGrid/types";
import CircularProgress from "../../Unknown/CircularProgress";

export type AppointmentPaymentListCardProps = {
  isPaymentButtonShown: boolean;
  remainingBalance: string;
  totalPrice: string;
  payments: AppointmentPayment[];
  refunds: AppointmentRefund[];
  isRefundDue: boolean;
  isReceiptReady: boolean;
  isReceiptDownloading: boolean;
  isReceiptSending: boolean;
  couponCode?: string;
  handleAddClick: () => void;
  handleAdjustPriceClick: () => void;
  handleOpenRefundDialog: (payment: AppointmentPayment) => void;
  handleViewReceiptClick: () => void;
  handleSendReceiptClick: () => void;
};

const AppointmentPaymentListCard: FC<AppointmentPaymentListCardProps> = ({
  isPaymentButtonShown,
  totalPrice,
  isReceiptReady,
  remainingBalance,
  payments,
  refunds,
  isRefundDue,
  isReceiptDownloading,
  isReceiptSending,
  couponCode,
  handleAddClick,
  handleAdjustPriceClick,
  handleOpenRefundDialog,
  handleViewReceiptClick,
  handleSendReceiptClick,
}) => {
  const { translations } = useTranslations();
  const classes = useStyles();
  const {
    title,
    btnAdd,
    btnAdjust,
    btnViewReceipt,
    btnSendReceipt,
    couponTitle,
  } = translations;
  const { role } = useParams<{ role: DatabaseEnum["center_user_role"] }>();

  const isUserAllowedToPaymentAction =
    !!role &&
    ["receptionist", "regional_admin", "super_admin", "technologist"].includes(
      role,
    );

  const balanceTitle = isRefundDue
    ? translations.refundDue
    : translations.outstandingBalance;

  return (
    <Card sx={{ minHeight: 250 }}>
      <CardHeader
        title={title}
        action={
          <Box>
            {!!isUserAllowedToPaymentAction && (
              <Box
                display="flex"
                justifyContent="end"
                flexDirection={{ xs: "column", lg: "row" }}
                gap={3}
                pl={3}
              >
                {isReceiptReady && (
                  <>
                    <Button
                      sx={{ px: 3 }}
                      variant="outlined"
                      disabled={isReceiptDownloading}
                      onClick={handleViewReceiptClick}
                      startIcon={
                        isReceiptDownloading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : undefined
                      }
                    >
                      {btnViewReceipt}
                    </Button>
                    <Button
                      sx={{ px: 3 }}
                      variant="outlined"
                      disabled={isReceiptSending}
                      onClick={handleSendReceiptClick}
                      startIcon={
                        isReceiptSending ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : undefined
                      }
                    >
                      {btnSendReceipt}
                    </Button>
                  </>
                )}
                <Button
                  sx={{ px: 3 }}
                  variant="contained"
                  onClick={handleAdjustPriceClick}
                >
                  {btnAdjust}
                </Button>
              </Box>
            )}
          </Box>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={4}
            >
              <Box flex={1}>
                <Typography classes={{ root: classes.labelRoot }}>
                  {translations.totalPrice}
                </Typography>
                <Typography variant="body2">{totalPrice}</Typography>
              </Box>
              <Box flex={1}>
                <Typography classes={{ root: classes.labelRoot }}>
                  {balanceTitle}
                </Typography>
                <Typography variant="body2">{remainingBalance}</Typography>
              </Box>
              {couponCode && (
                <Box flex={1}>
                  <Typography classes={{ root: classes.labelRoot }}>
                    {couponTitle}
                  </Typography>
                  <Typography variant="body2">{couponCode}</Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography classes={{ root: classes.labelRoot }}>
              {translations.title}
            </Typography>
            {!!isPaymentButtonShown && (
              <Button
                variant="contained"
                size="small"
                onClick={handleAddClick}
                className={classes.btnAdd}
              >
                {btnAdd}
              </Button>
            )}
          </Grid>
          <Grid item xs={12} mt={-4}>
            <AppointmentPaymentGrid
              payments={payments}
              handleOpenRefundDialog={handleOpenRefundDialog}
            />
          </Grid>
          {!!refunds.length && (
            <>
              <Grid item xs={12} mt={4}>
                <Typography classes={{ root: classes.labelRoot }}>
                  {translations.refunds}
                </Typography>
              </Grid>
              <Grid item xs={12} mt={-4}>
                <AppointmentRefundGrid refunds={refunds} />
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AppointmentPaymentListCard;
